import type { Game } from '@/packages/sxm/sports/games/GamesResponse';
import type { Race } from '@/packages/sxm/sports/races';
import type { Event } from '@/packages/sxm/sports/events';
import type { LeagueGameEntry, Teams, Leagues } from '@/services/contentful/contentTypes/UpcomingSports';
import type { GameCardProps, Team } from '@/ui-components/molecules/GameCard/GameCard';
import ImageOptimization from '@/services/image_optimization';
import { resolveChannel } from '@/packages/sxm/sports/channelWaterfall';

export type GameRaceEvent = Game | Race | Event;

export interface GameCardMappingContext {
    teams: Teams;
    leagues: Leagues;
    hasCopyright: boolean;
}

function allowsContrastingColors(logoUrl: string): boolean {
    return logoUrl.endsWith('.svg') && logoUrl.includes('/icons/');
}

export function hasGameCopyright(entries: LeagueGameEntry[], teams: Teams, leagues: Leagues): boolean {
    return entries.some((leagueEntry) =>
        leagueEntry.games.some((game: GameRaceEvent) => {
            if ('awayTeam' in game && 'homeTeam' in game) {
                const leagueData = leagues[game.homeLeagueAbbreviation ?? ''];
                const awayTeamData = teams[leagueData.abbreviation + game.awayTeamAbbreviation];
                const homeTeamData = teams[leagueData.abbreviation + game.homeTeamAbbreviation];
                return Boolean(awayTeamData?.logo?.copyrightText || homeTeamData?.logo?.copyrightText);
            } else if ('eventName' in game) {
                const eventTeamData = leagues[game.homeLeagueAbbreviation ?? ''];
                return Boolean(eventTeamData?.logo?.copyrightText);
            } else if ('raceOrDriverName' in game) {
                const eventTeamData = leagues[game.seriesKey ?? ''];
                return Boolean(eventTeamData?.logo?.copyrightText);
            }
            return false;
        }),
    );
}

export function mapGameToCardProps(
    game: GameRaceEvent,
    { teams, leagues, hasCopyright }: GameCardMappingContext,
): GameCardProps | undefined {
    if ('awayTeam' in game && 'homeTeam' in game) {
        const leagueData = leagues[game.homeLeagueAbbreviation ?? ''];
        const leagueTitle = leagueData.isSoccer ? 'SOCCER' : (leagueData.title ?? '');

        const awayTeamData = teams[leagueData.abbreviation + (game.awayTeamAbbreviation ?? '')];
        const homeTeamData = teams[leagueData.abbreviation + (game.homeTeamAbbreviation ?? '')];

        const awayTeamName = awayTeamData?.teamName;
        const homeTeamName = homeTeamData?.teamName;

        const awayLogoUrl =
            (awayTeamData?.logo?.url && ImageOptimization({ src: awayTeamData?.logo?.url, width: 150, height: 77 })) ??
            '';
        const awayLogoCopyright = hasCopyright ? awayTeamData?.logo?.copyrightText || ' ' : undefined;
        const homeLogoUrl =
            (homeTeamData?.logo?.url && ImageOptimization({ src: homeTeamData?.logo?.url, width: 150, height: 77 })) ??
            '';
        const homeLogoCopyright = hasCopyright ? homeTeamData?.logo?.copyrightText || ' ' : undefined;
        const leaguePageUrl = leagueData?.leaguePageUrl ?? '#';
        const awayTeamPageUrl = awayTeamData?.pageUrl ? awayTeamData?.pageUrl : leaguePageUrl;
        const homeTeamPageUrl = homeTeamData?.pageUrl ? homeTeamData?.pageUrl : leaguePageUrl;

        const awayResolved = resolveChannel(game, 'away');
        const homeResolved = resolveChannel(game, 'home');

        const isSoccer = leagueData.isSoccer;

        const awayTeam: Team = {
            title: awayTeamName ?? '',
            subtitle: 'away',
            channel: awayResolved.channel?.toString(),
            isOnline: awayResolved.isOnline,
            link: { href: awayTeamPageUrl, ['aria-label']: game.awayTeam || '' },
            image: { src: awayLogoUrl, alt: game.awayTeam ?? '' },
            backgroundColor: awayTeamData?.color || '',
            nationalChannel: game.nationalStream?.toString(),
            spanishChannel: game.spanishStream?.toString(),
            channelStream: game.awayInternetStream?.toString(),
            nationalStream: game.nationalInternetStream?.toString(),
            spanishStream: game.spanishInternetStream?.toString(),
            channelStreamLink: game.awayStreamLink?.toString(),
            nationalStreamLink: game.nationalStreamLink?.toString(),
            spanishStreamLink: game.spanishStreamLink?.toString(),
        };

        const homeTeam: Team = {
            title: homeTeamName ?? '',
            subtitle: 'home',
            channel: homeResolved.channel?.toString(),
            isOnline: homeResolved.isOnline,
            link: { href: homeTeamPageUrl, ['aria-label']: game.homeTeam || '' },
            image: { src: homeLogoUrl, alt: game.homeTeam ?? '' },
            backgroundColor: homeTeamData?.color || '',
            nationalChannel: game.nationalStream?.toString(),
            spanishChannel: game.spanishStream?.toString(),
            channelStream: game.homeInternetStream?.toString(),
            nationalStream: game.nationalInternetStream?.toString(),
            spanishStream: game.spanishInternetStream?.toString(),
            channelStreamLink: game.homeStreamLink?.toString(),
            nationalStreamLink: game.nationalStreamLink?.toString(),
            spanishStreamLink: game.spanishStreamLink?.toString(),
        };

        return {
            dateTime: game.gameDate ? new Date(game.gameDate) : new Date(),
            teams: isSoccer ? [homeTeam, awayTeam] : [awayTeam, homeTeam],
            link: { href: leaguePageUrl },
            linkText: `${leagueTitle} schedule`,
            firstCopyright: awayLogoCopyright,
            secondCopyright: homeLogoCopyright,
        };
    }

    if ('eventName' in game) {
        const eventTeamData = leagues[game.homeLeagueAbbreviation ?? ''];
        const eventTitle = eventTeamData?.title ?? '';
        const eventLogoUrl =
            (eventTeamData?.logo?.url &&
                ImageOptimization({ src: eventTeamData?.logo?.url, width: 150, height: 72 })) ??
            '';
        const eventLogoCopyright = hasCopyright ? eventTeamData?.logo?.copyrightText || ' ' : undefined;
        const leaguePageUrl = eventTeamData?.leaguePageUrl ? eventTeamData?.leaguePageUrl : '#';

        const eventResolved = resolveChannel(game, 'national');

        return {
            dateTime: game.gameDate ? new Date(game.gameDate) : new Date(),
            teams: [
                {
                    title: game.eventName ?? '',
                    subtitle: game.venueName || '',
                    channel: eventResolved.channel?.toString(),
                    isOnline: eventResolved.isOnline,
                    link: {
                        href: leaguePageUrl,
                        ['aria-label']: eventTitle || '',
                    },
                    image: {
                        src: eventLogoUrl,
                        alt: 'race',
                    },
                    backgroundColor: eventTeamData?.color || '#000000',
                    nationalChannel: game.nationalStream?.toString(),
                    spanishChannel: game.spanishStream?.toString(),
                    nationalStream: game.nationalInternetStream?.toString(),
                    spanishStream: game.spanishInternetStream?.toString(),
                    nationalStreamLink: game.nationalStreamLink?.toString(),
                    spanishStreamLink: game.spanishStreamLink?.toString(),
                },
            ],
            link: { href: leaguePageUrl },
            linkText: `${eventTitle} schedule`,
            firstCopyright: eventLogoCopyright,
            // In case content author doesn't want this overriding (We may revisit if frequent):
            useContrastingImageColors: allowsContrastingColors(eventLogoUrl),
        };
    }

    if ('raceOrDriverName' in game) {
        const eventTeamData = leagues[game.seriesKey ?? ''];
        const eventTitle = 'NASCAR';
        const eventLogoUrl =
            (eventTeamData?.logo?.url &&
                ImageOptimization({ src: eventTeamData?.logo?.url, width: 150, height: 72 })) ??
            '';
        const eventLogoCopyright = hasCopyright ? eventTeamData?.logo?.copyrightText || ' ' : undefined;
        const leaguePageUrl = eventTeamData?.leaguePageUrl ? eventTeamData?.leaguePageUrl : '#';

        const raceResolved = resolveChannel(game, 'national');

        return {
            dateTime: game.startDateTime ? new Date(game.startDateTime) : new Date(),
            teams: [
                {
                    title: game.raceOrDriverName ?? '',
                    subtitle: game.venueOrTeamName || '',
                    channel: raceResolved.channel?.toString(),
                    isOnline: raceResolved.isOnline,
                    link: {
                        href: leaguePageUrl,
                        ['aria-label']: eventTitle || '',
                    },
                    image: {
                        src: eventLogoUrl,
                        alt: 'race',
                    },
                    backgroundColor: eventTeamData?.color || '#000000',
                    nationalChannel: game.nationalStream?.toString(),
                    spanishChannel: game.spanishStream?.toString(),
                    nationalStream: game.nationalInternetStream?.toString(),
                    spanishStream: game.spanishInternetStream?.toString(),
                    nationalStreamLink: game.nationalStreamLink?.toString(),
                    spanishStreamLink: game.spanishStreamLink?.toString(),
                },
            ],
            link: { href: leaguePageUrl },
            linkText: `${eventTitle} schedule`,
            firstCopyright: eventLogoCopyright,
        };
    }

    return undefined;
}

export function deriveUpcomingSportsCards(
    entries: LeagueGameEntry[],
    context: GameCardMappingContext & { now: number },
): GameCardProps[] {
    const { now, ...mappingContext } = context;

    return entries.flatMap((leagueEntry) => {
        const activeGames = leagueEntry.games
            .filter((game: GameRaceEvent) => {
                const endDate =
                    ('endDateTime' in game && game.endDateTime) || ('gameEndDate' in game && game.gameEndDate);
                if (!endDate) return true;
                const endTs = Date.parse(endDate);
                return Number.isNaN(endTs) || endTs > now;
            })
            .slice(0, leagueEntry.displayCount);

        return activeGames
            .map((game) => mapGameToCardProps(game, mappingContext))
            .filter((cardProps): cardProps is GameCardProps => cardProps !== undefined);
    });
}
